/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState, useContext, useMemo, useEffect, useRef } from 'react';
import {
    CpdsSweepstakesFormsToolkitsInputFieldsWrapper,
    CpdsSweepstakesFormsToolkitsLabelWrapper,
    CpdsSweepstakesFormsToolkitsLabel,
    CpdsSweepstakesFormsToolkitsSelect,
    CpdsSweepstakesFormsToolkitsHelperTextWrapper,
    CpdsSweepstakesFormsToolkitsHelperLabel,
    CpdsSweepstakesFormsToolkitsSelectWrapper,
    CpdsSweepstakesFormsToolkitsSelectOptionWrapper,
    Arrow,
    CpdsSweepstakesSelect,
    CpdsSweepstakesSelectButton,
    CpdsSweepstakesOptionList,
    CpdsSweepstakesOptionItem
} from '../styles/cpds-sweepstakes-forms-toolkits-inputs-style';
import { CpdsSweepstakesSelectInputProps, BorderType } from '../typings';
import { Cpds } from '@Cpds.Ui';
import { ThemeContext } from 'styled-components';
import { themeToken } from '../../utils/cpds-utils';
import styled, { css } from 'styled-components';

const isHtml = (str: string): boolean => {
    const doc = new DOMParser().parseFromString(str, 'text/html');
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

const Dropdown: React.FC<CpdsSweepstakesSelectInputProps> = ({
    inputId = "",
    domRef,
    exClasses = '',
    styleOverrides = {},
    inputWidth,
    isDisabled,
    LabelCopy,
    LabelIcon,
    LabelIconSize = { width: '10', height: '10' },
    Options= [],
    HelperCopy = '',
    HelperIcon = 'information',
    HelperIconSize = { width: '20', height: '20' },
    isError = false,
    ErrorIcon = 'information',
    ErrorIconSize = { width: '20', height: '20' },
    ErrorMessage = '',
    isOverRideTypography = false,
    PlaceholderCopy = 'Select an option',  // Default placeholder
    getValue = () => { },
    onChange = () => { },
    SelectedValue = '',
    borderType = BorderType.RoundPill,
    scrollHeight = '174px',
    showSelectedValue = false,
    setChange = '',
    ...rest
}) => {
    const { themeColorMode } = rest;
    const [isOpen, setIsOpen] = useState(false);
    const themeContext: any = useContext(ThemeContext);
    const cpds = themeContext?.styles?.cpds;
    const token = useMemo(() => themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode).token, [cpds, rest]);
    const scrollbarRef = useRef(null);

    useEffect(() => {
        handleOptionClick(setChange);
      }, [setChange]);

    const getLabelCopy = useCallback(
        (LabelCopy) => {
            if (isHtml(LabelCopy)) {
                return (
                    <CpdsSweepstakesFormsToolkitsLabel
                        className="inputbox-top-label cpds-body-roman-medium-08"
                        isDisabled={isDisabled}
                        themeColorMode={themeColorMode}
                        isOverRideTypography={isOverRideTypography}
                        dangerouslySetInnerHTML={{ __html: LabelCopy }}
                    ></CpdsSweepstakesFormsToolkitsLabel>
                );
            } else {
                return (
                    <CpdsSweepstakesFormsToolkitsLabel
                        className="inputbox-top-label cpds-body-roman-medium-08"
                        isDisabled={isDisabled}
                        themeColorMode={themeColorMode}
                        isOverRideTypography={isOverRideTypography}
                    >
                        {LabelCopy}
                    </CpdsSweepstakesFormsToolkitsLabel>
                );
            }
        },
        [isDisabled, themeColorMode, isOverRideTypography]
    );

    const [selectedOption, setSelectedOption] = useState(SelectedValue);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
    const dropdownRef = domRef ?? useRef<HTMLDivElement>(null);

    useEffect(() => {
        getValue(selectedOption); // Pass the current selected value to the getValue callback
    }, [selectedOption, getValue]);

    useEffect(() => {
        if (scrollbarRef.current) {
        //   scrollbarRef.current.updateScroll();  // Recalculate scrollbar dimensions
        }
    }, [isOpen]);

    const onDropDownChange = useCallback(
        (e) => {
            handleOptionClick(e);
            handleKeyDown(e);
        },
        [onChange]
    );

    const toggleDropdown = () => setIsOpen(!isOpen);

    const handleOptionClick = (option: string) => {
        setSelectedOption(option);
        setIsOpen(false); // Close dropdown after selecting an option
        setFocusedOptionIndex(null);

        if(onChange){
            onChange(option as unknown as React.ChangeEvent<HTMLInputElement>)
        }
    };

      // Close dropdown if clicked outside
    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };


    }, [isOpen]);

    useEffect(() => {
        setIsOpen(false); // Close dropdown after selecting an option
    }, [selectedOption])

    // Function to scroll the focused option into view
    const scrollIntoView = (index: number) => {       
        const optionElement = document.getElementById(`dropdown-option-${index}`);
        if (optionElement && scrollbarRef.current) {
            const container = scrollbarRef.current.contentElement; // Get the scrollable content element
            const optionTop = optionElement.offsetTop;
            const optionHeight = optionElement.offsetHeight;
            const scrollTop = container.scrollTop;
            const containerHeight = scrollbarRef.current.scrollValues.clientHeight;

            // Check if the option is out of view
            const isAboveView = optionTop < scrollTop;
            const isBelowView = optionTop + optionHeight > scrollTop + containerHeight;

            if(optionTop === 0){
                scrollbarRef.current.scrollTo(0, optionTop);
            }

            // Scroll only if out of view
            if (isAboveView) {
                container.scrollTop = optionTop; // Scroll up to the option's top
                scrollbarRef.current.scrollTo(0, optionTop);
            } else if (isBelowView) {
                container.scrollTop = optionTop + optionHeight - containerHeight; // Scroll down to option's bottom
                scrollbarRef.current.scrollTo(0, optionTop + optionHeight - containerHeight);
            }
        }
    };
    

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement | HTMLUListElement>) => {
        if (!isOpen) return;

        switch (e.key) {
        case 'ArrowDown':
            e.preventDefault();
            setFocusedOptionIndex((prevIndex) => {
                const nextIndex = prevIndex === null || prevIndex === Options.length - 1 ? 0 : prevIndex + 1;
                scrollIntoView(nextIndex);  // Scroll the next option into view
                return nextIndex;
            });
            break;
        case 'ArrowUp':
            e.preventDefault();
            setFocusedOptionIndex((prevIndex) => {
                const nextIndex = prevIndex === null || prevIndex === 0 ? Options.length - 1 : prevIndex - 1;
                scrollIntoView(nextIndex);  // Scroll the previous option into view
                return nextIndex;
            });
            break;
        case 'Enter':
            if (focusedOptionIndex !== null) {
                handleOptionClick(showSelectedValue ? Options[focusedOptionIndex].value : Options[focusedOptionIndex].label);
            }
            break;
        case 'Escape':
            setIsOpen(false);
            setFocusedOptionIndex(null);
            break;
        default:
            break;
        }
    };

    return (
        <CpdsSweepstakesFormsToolkitsSelectWrapper
            className={`cpds-sweepstake-input-select ${exClasses} ${isError ? 'invalid' : ''}`}
            ref={dropdownRef}
            style={styleOverrides}
            inputWidth={inputWidth}
        >
            {LabelCopy && (
                <CpdsSweepstakesFormsToolkitsLabelWrapper
                    className="cpds-body-roman-medium-08"
                >
                    {getLabelCopy(LabelCopy)}
                    {LabelIcon && (
                        <Cpds.Icon
                            id="ico-1"
                            name={LabelIcon}
                            customSize={LabelIconSize}
                            color={
                                isDisabled
                                    ? token?.input_fields_label_disable_icon_color || '#31374F'
                                    : token?.input_fields_label_icon_color || '#31374F'
                            }
                        />
                    )}
                </CpdsSweepstakesFormsToolkitsLabelWrapper>
            )}

            <CpdsSweepstakesSelect
                className="cpds-meta-roman-book-09"
            >
                <CpdsSweepstakesSelectButton 
                    onClick={toggleDropdown} 
                    onKeyDown={handleKeyDown} 
                    tabIndex={0}
                    id={inputId}
                    isError={isError}
                    isOpen={isOpen}
                    disabled={isDisabled}
                    value={selectedOption}
                    borderType={borderType}
                >
                    {selectedOption}
                    <Arrow isOpen={isOpen} />
                </CpdsSweepstakesSelectButton>
                {isOpen && (
                    <Cpds.scrollBar
                        domRef={scrollbarRef}
                        styleOverrides={
                            css`
                                width: 100% !important;
                                height: ${scrollHeight} !important;
                                border: 1px #D1D5E2 solid;
                                border-radius: 16px;
                                box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.07);
                                overflow: hidden !important;
                                margin-top: 4px;
                            `
                        }
                    >    
                        <CpdsSweepstakesOptionList onKeyDown={onDropDownChange} tabIndex={0}>
                            {Options.map((option, index) => (
                                <CpdsSweepstakesOptionItem
                                    key={option.value || index}
                                    isFocused={focusedOptionIndex === index}
                                    onClick={() => onDropDownChange(showSelectedValue ? option?.value : option?.label)}
                                    onKeyDown={onDropDownChange}
                                    id={`dropdown-option-${index}`}  // Unique ID for each option
                                >
                                {option?.label}
                                </CpdsSweepstakesOptionItem>
                            ))}
                        </CpdsSweepstakesOptionList>
                    </Cpds.scrollBar>
                )}
            </CpdsSweepstakesSelect>
            {(HelperCopy || ErrorMessage) && (
                <CpdsSweepstakesFormsToolkitsHelperTextWrapper>
                    {(isError && ErrorIcon) && (
                        <Cpds.Icon
                            id="ico-3"
                            color={
                                isError
                                    ? token?.input_fields_error_label_color
                                    : isDisabled
                                        ? token?.input_fields_helper_icon_disable_color
                                        : token?.input_fields_helper_label_color
                            }
                            name={isError ? ErrorIcon : ''}
                            customSize={ErrorIconSize}
                        />
                    )}

                    {(HelperIcon && HelperCopy !== '' && !isError) && (
                        <Cpds.Icon
                            id="ico-3"
                            color={
                                isError
                                    ? token?.input_fields_error_label_color
                                    : isDisabled
                                        ? token?.input_fields_helper_icon_disable_color
                                        : token?.input_fields_helper_label_color
                            }
                            name={HelperIcon}
                            customSize={HelperIconSize}
                        />
                    )}
                    <CpdsSweepstakesFormsToolkitsHelperLabel 
                        isError={isError} 
                        isDisabled={isDisabled} 
                        themeColorMode={themeColorMode} 
                        isOverRideTypography={isOverRideTypography}
                        className="cpds-meta-roman-book-10"
                    >
                        {isError ? ErrorMessage : HelperCopy}
                    </CpdsSweepstakesFormsToolkitsHelperLabel>
                </CpdsSweepstakesFormsToolkitsHelperTextWrapper>
            )}
        </CpdsSweepstakesFormsToolkitsSelectWrapper>
    );
};

export default Dropdown;
